import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { AppLayout } from '@/components/layout'
import { FullPageLoader } from '@/components/ui/full-page-loader'
import { InventoryPage } from '@/pages/inventory/inventory.page'
import { InventoryTab } from '@/pages/inventory/inventory-tabs'
import { RecipePage } from '@/pages/inventory/recipe.page'
import { ProductsTab } from '@/pages/products/products-tabs'

import { RequireAuth } from './require-auth'
import { RequireGuest } from './require-guest'
import { paths } from './paths'

/*
 * Every page is fetched the first time its route is opened rather than shipped
 * in the one bundle up front. A merchant who only ever takes orders should not
 * download the POS floor, the reports and the staff module to do it.
 *
 * `lazy` wants a default export and the pages export by name, hence the
 * `.then` on each. `ProductsTab` stays a static import: it is a constant in its
 * own file precisely so this module can read it without pulling in the page.
 */
const ChangePasswordPage = lazy(() =>
  import('@/pages/auth/change-password.page').then((module) => ({
    default: module.ChangePasswordPage,
  })),
)
const LoginPage = lazy(() =>
  import('@/pages/auth/login.page').then((module) => ({ default: module.LoginPage })),
)
const BusinessPage = lazy(() =>
  import('@/pages/business/business.page').then((module) => ({ default: module.BusinessPage })),
)
const DiscountsPage = lazy(() =>
  import('@/pages/catalog/discounts.page').then((module) => ({ default: module.DiscountsPage })),
)
const CustomersPage = lazy(() =>
  import('@/pages/customers/customers.page').then((module) => ({ default: module.CustomersPage })),
)
const DashboardPage = lazy(() =>
  import('@/pages/dashboard/dashboard.page').then((module) => ({ default: module.DashboardPage })),
)
const NotFoundPage = lazy(() =>
  import('@/pages/errors/not-found.page').then((module) => ({ default: module.NotFoundPage })),
)
const OrdersPage = lazy(() =>
  import('@/pages/orders/orders.page').then((module) => ({ default: module.OrdersPage })),
)
const OverviewPage = lazy(() =>
  import('@/pages/overview/overview.page').then((module) => ({ default: module.OverviewPage })),
)
const PaymentReturnPage = lazy(() =>
  import('@/pages/payments/payment-return.page').then((module) => ({
    default: module.PaymentReturnPage,
  })),
)
const PaymentsPage = lazy(() =>
  import('@/pages/payments/payments.page').then((module) => ({ default: module.PaymentsPage })),
)
const PosPage = lazy(() =>
  import('@/pages/pos/pos.page').then((module) => ({ default: module.PosPage })),
)
const ProductsPage = lazy(() =>
  import('@/pages/products/products.page').then((module) => ({ default: module.ProductsPage })),
)
const ReportsPage = lazy(() =>
  import('@/pages/reports/reports.page').then((module) => ({ default: module.ReportsPage })),
)
const OperationsPage = lazy(() =>
  import('@/pages/settings/operations.page').then((module) => ({ default: module.OperationsPage })),
)
const AcceptInvitationPage = lazy(() =>
  import('@/pages/staff/accept-invitation.page').then((module) => ({
    default: module.AcceptInvitationPage,
  })),
)
const StaffPage = lazy(() =>
  import('@/pages/staff/staff.page').then((module) => ({ default: module.StaffPage })),
)
const StorefrontPage = lazy(() =>
  import('@/pages/storefront/storefront.page').then((module) => ({
    default: module.StorefrontPage,
  })),
)
const TablesPage = lazy(() =>
  import('@/pages/tables/tables.page').then((module) => ({ default: module.TablesPage })),
)

export function AppRoutes() {
  return (
    // Catches the standalone screens — sign-in, password change, 404. Pages
    // inside the shell suspend against the boundary in `AppLayout` instead,
    // so the app bar stays put while the next page loads.
    <Suspense fallback={<FullPageLoader />}>
      <Routes>
        <Route path={paths.root} element={<Navigate to={paths.dashboard} replace />} />

        <Route
          path={paths.login}
          element={
            <RequireGuest>
              <LoginPage />
            </RequireGuest>
          }
        />

        <Route
          path={paths.changePassword}
          element={
            <RequireAuth allowPasswordChangePending>
              <ChangePasswordPage />
            </RequireAuth>
          }
        />

        {/* Public on purpose, and outside the shell. Most invitees have no
            account yet, so a sign-in wall here was a dead end; the page reads
            the invitation and offers sign-in or sign-up itself. */}
        <Route path={paths.acceptInvitation} element={<AcceptInvitationPage />} />

        <Route
          element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }
        >
          <Route path={paths.dashboard} element={<DashboardPage />} />
          <Route path={paths.overview} element={<OverviewPage />} />

          {/* Catalogue */}
          <Route path={paths.products} element={<ProductsPage />} />
          {/* Categories and Modifiers were folded into Products. Kept as real
            routes rather than redirects so an old bookmark still lands on the
            tab it meant, not merely somewhere in the right module. */}
        <Route
          path={paths.categories}
          element={<ProductsPage initialTab={ProductsTab.Categories} />}
        />
        <Route
          path={paths.modifiers}
          element={<ProductsPage initialTab={ProductsTab.ChoiceGroups} />}
        />
        <Route path={paths.discounts} element={<DiscountsPage />} />
        {/* One route per tab, all on the same page: the URL is what picks the tab. */}
        <Route
          path={paths.inventoryOverview}
          element={<InventoryPage tab={InventoryTab.Overview} />}
        />
        <Route path={paths.inventory} element={<InventoryPage tab={InventoryTab.StockItems} />} />
        <Route
          path={paths.inventoryAdjustments}
          element={<InventoryPage tab={InventoryTab.Adjustments} />}
        />
        <Route path={paths.inventoryCounts} element={<InventoryPage tab={InventoryTab.Counts} />} />
        <Route
          path={paths.inventoryRecipes}
          element={<InventoryPage tab={InventoryTab.Recipes} />}
        />
        <Route path={paths.inventoryRecipeNew} element={<RecipePage />} />
        <Route path={paths.inventoryRecipe} element={<RecipePage />} />
        <Route
          path={paths.inventoryModifiers}
          element={<InventoryPage tab={InventoryTab.Modifiers} />}
        />
        <Route path={paths.inventoryUnits} element={<InventoryPage tab={InventoryTab.Units} />} />
        <Route
          path={paths.inventoryLocations}
          element={<InventoryPage tab={InventoryTab.Locations} />}
        />
          <Route
            path={paths.categories}
            element={<ProductsPage initialTab={ProductsTab.Categories} />}
          />
          <Route
            path={paths.modifiers}
            element={<ProductsPage initialTab={ProductsTab.ChoiceGroups} />}
          />
          <Route path={paths.discounts} element={<DiscountsPage />} />

          {/* Trading */}
          <Route path={paths.orders} element={<OrdersPage />} />
          <Route path={paths.customers} element={<CustomersPage />} />
          <Route path={paths.tables} element={<TablesPage />} />
          <Route path={paths.pos} element={<PosPage />} />

          {/* The business */}
          <Route path={paths.storefront} element={<StorefrontPage />} />
          <Route path={paths.business} element={<BusinessPage />} />
          <Route path={paths.operations} element={<OperationsPage />} />
          {/* One route for the module and every report in it, so the page — and
            the Location, dates and channel it holds for every report — stays
            mounted while the reader moves between them. */}
          <Route path={paths.reportsModule} element={<ReportsPage />} />

          <Route path={paths.staff} element={<StaffPage />} />
          <Route path={paths.payments} element={<PaymentsPage />} />
          <Route path={paths.paymentReturn} element={<PaymentReturnPage />} />
          <Route path={paths.paymentReturnOutcome} element={<PaymentReturnPage />} />
        </Route>

        <Route path={paths.notFound} element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  )
}
